import React, {
    ChangeEvent,
    FC,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useRef
} from 'react'
import { AnyColor, Colord, colord, extend } from 'colord'
import namesPlugin from 'colord/plugins/names'
import { Subject, Subscription } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import useIsMountedRef from 'use-is-mounted-ref'
import {
    Controller,
    ControllerRenderProps,
    useFormContext,
    UseFormMethods
} from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { Rules } from '../../../../React/Common/Types/Types'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { ArrayUtils } from '../../../../NonReact/utils/ArrayUtils'
import { CommonFormContext, FormContextType } from '../../../../React/Common/Contexts/CommonFormContext'
import { UpdateTo } from '../../../../React/CMEAddModify/Types'
import { colourContrast, lightenOrDarken } from '../../../../NonReact/utils/ColourUtils'

extend([namesPlugin])

type Props = {
    name: string
    defaultValue?: string
    label?: string
    alphaTransparency?: boolean | [number, number]
    rules?: Rules
    updateTo?: UpdateTo[]
    disabled?: boolean
}

export const ColourPicker: FC<Props> = ({
    name,
    defaultValue,
    label,
    alphaTransparency,
    rules,
    updateTo,
    disabled
}: Props): ReactElement => {
    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    const { control, setValue, watch }: UseFormMethods = useFormContext()

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const changeSub: MutableRefObject<Nullable<Subscription>> = useRef<Nullable<Subscription>>()
    const changeSubject: MutableRefObject<Subject<string>> = useRef<Subject<string>>(new Subject())

    // useWatch caused a bug where the colour picker would forget the colour when element was de-rendered.
    const currentColor: Definable<string> = watch(name)

    const getColourAsHex = useCallback(
        (color?: AnyColor): string => {
            const newColor: Colord = colord(color ?? EMPTY_STRING)

            if (typeof alphaTransparency === 'boolean') {
                if (alphaTransparency) {
                    return newColor.toHex()
                }
            } else if (!ArrayUtils.isEmpty(alphaTransparency)) {
                const [min, max]: [number, number] = alphaTransparency
                const transparency: number = newColor.alpha()

                if (transparency >= min && transparency <= max) {
                    return newColor.toHex()
                }

                return newColor.alpha(transparency < min ? min : max).toHex()
            }

            return newColor.toHex()
        },
        [alphaTransparency]
    )

    const defaultNormalised: string = getColourAsHex(defaultValue ?? EMPTY_STRING)

    useEffect((): void => {
        changeSub.current = changeSubject.current.pipe(debounceTime(100)).subscribe((newColour: string): void => {
            if (isMounted.current) {
                const newValue: Definable<string> = !colord(newColour ?? EMPTY_STRING).isValid()
                    ? defaultNormalised
                    : getColourAsHex(newColour)

                setValue(name, newValue)

                if (newValue !== defaultNormalised) {
                    commonForm?.handleFormChange?.(name)
                } else {
                    commonForm?.resetDirtyField?.(name)
                }
            }
        })
    }, [changeSubject.current])

    useEffect((): void => {
        if (updateTo) {
            updateTo.forEach(({ name: updateName, shouldContrast, lightenOrDarken: ldAmount }: UpdateTo): void => {
                if (isMounted.current) {
                    if (commonForm?.dirtyFields?.[updateName]) {
                        return
                    }

                    const currentColour: Definable<string> = getColourAsHex(currentColor)

                    if (shouldContrast) {
                        setValue(updateName, colourContrast(currentColour))
                    } else if (ldAmount) {
                        setValue(updateName, lightenOrDarken(ldAmount)(currentColour))
                    } else {
                        setValue(updateName, currentColour)
                    }
                }
            })
        }
    }, [currentColor])

    useEffect(
        (): (() => void) => (): void => {
            changeSub.current?.unsubscribe()
            changeSubject.current?.complete()
        },
        []
    )

    return (
        <div className='colour-picker'>
            <Controller
                name={name}
                defaultValue={defaultNormalised}
                control={control}
                rules={rules}
                disabled={disabled}
                render={({ value, onChange }: ControllerRenderProps<Record<string, unknown>>): ReactElement => (
                    <>
                        <div className='colour-picker-text-input-container'>
                            <label className='colour-picker-label' htmlFor={name}>
                                {label}
                            </label>
                            <div className='cptic-input-container'>
                                {/* We are using native input here as the React version are very laggy */}
                                <input
                                    id={name}
                                    className='colour-picker-text-input'
                                    type='text'
                                    disabled={disabled}
                                    onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                                        const newValue: string = event.target.value

                                        onChange(newValue)

                                        if (newValue !== defaultNormalised) {
                                            commonForm?.handleFormChange?.(name)
                                        } else {
                                            commonForm?.resetDirtyField?.(name)
                                        }
                                    }}
                                    onBlur={(event: React.FocusEvent<HTMLInputElement>): void => {
                                        const currentValue: string = event.target.value
                                        const newValue: Definable<string> = !colord(
                                            currentValue ?? EMPTY_STRING
                                        ).isValid()
                                            ? defaultNormalised
                                            : getColourAsHex(currentValue)

                                        onChange(newValue)

                                        if (newValue !== defaultNormalised) {
                                            commonForm?.handleFormChange?.(name)
                                        } else {
                                            commonForm?.resetDirtyField?.(name)
                                        }
                                    }}
                                    value={value as string}
                                />
                                {/* We are using native input here as the React version are very laggy */}
                                <input
                                    className='colour-picker-button'
                                    type='color'
                                    color={value as string}
                                    value={value as string}
                                    disabled={disabled}
                                    style={{ backgroundColor: value as string }}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                        changeSubject.current?.next(event.target.value)
                                    }
                                />
                            </div>
                        </div>
                    </>
                )}
            />
        </div>
    )
}
